import { format, isBefore, isToday, isYesterday } from 'date-fns';

import { Activity, ActivityEntity, ActivityType } from '@/types';
import { timestampToRelativeTimeFromNow } from '@/core/utils';

export const groupActivities = (activities: Activity[]): Activity[][] => {
  const grouped: Activity[][] = [];
  let currentGroup: Activity[] = [];
  let lastGroupKey: string | null = null;

  (activities || []).forEach((activity) => {
    const groupKey = timestampToRelativeTimeFromNow(
      new Date(activity.createdAt),
    );

    if (groupKey !== lastGroupKey) {
      if (currentGroup.length) {
        grouped.push(currentGroup);
      }
      currentGroup = [];
      lastGroupKey = groupKey;
    }

    currentGroup.push(activity);
  });

  if (currentGroup.length) {
    grouped.push(currentGroup);
  }

  return grouped;
};

export const formatActivityTimestamp = (date: Date): string => {
  if (isToday(date)) {
    return `Today ${format(date, 'h:mma')}`;
  }
  if (isYesterday(date)) {
    return `Yesterday ${format(date, 'h:mma')}`;
  }
  return format(date, 'MMM d, yyyy');
};

export type ActivityLinkTarget = {
  routeName: string;
  params: Record<string, string>;
};

/**
 * Route name + params for the entity a bell-menu row points at, or null when the
 * row isn't linkable: an unhandled entity, a workflow on a firm without advisory
 * access (workflows.show sits behind the IsAdvisoryFirm middleware, so linking it
 * would 404 rather than navigate), or a workflow the server flagged as not
 * navigable because the viewer can't reach its taxpayer. `isNavigable` must be
 * explicitly true, so a payload cached before this flag existed degrades to plain
 * text rather than to a 404 link. Kept separate from firmRoute() so the routing
 * decision is unit-testable without Ziggy's runtime config.
 */
export const getActivityLinkTarget = (
  activity: Activity,
  isAdvisoryFirm: boolean,
): ActivityLinkTarget | null => {
  // Checked before the entity branches: an analysis row is scoped to a taxpayer,
  // so it would otherwise land on the client page instead of the tab holding the
  // run the notification is about.
  if (activity.type === ActivityType.analysisCompleted) {
    return {
      routeName: 'taxpayers.analyses',
      params: { taxpayer: activity.entityPid, firm: activity.firmPid },
    };
  }

  if (activity.entity === ActivityEntity.engagement) {
    return {
      routeName: 'engagements.show',
      params: { engagementId: activity.entityPid, firm: activity.firmPid },
    };
  }

  if (activity.entity === ActivityEntity.taxpayer) {
    return {
      routeName: 'taxpayers.show',
      params: { taxpayer: activity.entityPid, firm: activity.firmPid },
    };
  }

  if (
    activity.entity === ActivityEntity.workflow &&
    isAdvisoryFirm &&
    activity.isNavigable === true
  ) {
    return {
      routeName: 'workflows.show',
      params: { workflow: activity.entityPid, firm: activity.firmPid },
    };
  }

  return null;
};

export const getActivitySubtitle = (activity: Activity): string => {
  // Entity wins over type on workflow rows: entity and type are validated
  // independently, so a workflow row can carry an engagement-only type and must not
  // borrow that type's engagement copy.
  if (activity.entity === ActivityEntity.workflow) {
    return getWorkflowActivitySubtitle(activity);
  }

  // Without this the row falls through to the file-upload default and reads
  // "1 files uploaded".
  if (activity.type === ActivityType.analysisCompleted) {
    return getAnalysisActivitySubtitle(activity);
  }

  if (activity.type === ActivityType.taxQuestionnaireCompleted) {
    return `${
      activity.filesCount === 1 ? '' : activity.filesCount
    } ${activity.filesCount === 1 ? 'Tax' : 'tax'} questionnaire${
      activity.filesCount > 1 ? 's' : ''
    } completed`;
  }

  if (activity.type === ActivityType.bankQuestionnaireCompleted) {
    return 'Bank account added for direct debit/withdrawal for tax payment';
  }

  if (activity.type === ActivityType.finalEfileAuthCompleted) {
    return 'Final e-file auth signatures completed';
  }

  if (activity.type === ActivityType.extensionEfileAuthCompleted) {
    return 'Extension e-file auth signatures completed';
  }

  return `${activity.filesCount} ${
    activity.filesCount === 1 ? 'file' : 'files'
  } uploaded`;
};

/**
 * Analysis rows word themselves from the run's terminal status. 'empty' is
 * terminal but produces no output, so it gets its own copy rather than inviting a
 * click through to a blank result the way "ready to view" does.
 */
const getAnalysisActivitySubtitle = (activity: Activity): string => {
  if (activity.analysisStatus === 'failed') {
    return 'Analysis failed — try running it again';
  }

  if (activity.analysisStatus === 'empty') {
    return 'Analysis finished with nothing to show';
  }

  return 'Analysis ready to view';
};

/**
 * Workflow rows name their sub-entity from the activity's metadata, which isn't
 * guaranteed to carry it (metadata is nullable, and POST /api/activities accepts
 * a workflow activity without it), so both single-row cases degrade to the bare
 * label rather than interpolating a missing value.
 */
const getWorkflowActivitySubtitle = (activity: Activity): string => {
  if (activity.type === ActivityType.fileUploaded) {
    if (activity.filesCount > 1) {
      return `${activity.filesCount} documents uploaded`;
    }

    return activity.requestName
      ? `Document uploaded: ${activity.requestName}`
      : 'Document uploaded';
  }

  if (activity.type === ActivityType.workflowTaskCompleted) {
    if (activity.filesCount > 1) {
      return `${activity.filesCount} tasks completed`;
    }

    const label =
      activity.taskType === 'questionnaire'
        ? 'Client submitted questionnaire'
        : 'Task completed';

    return activity.taskTitle ? `${label}: ${activity.taskTitle}` : label;
  }

  return `${activity.filesCount} ${
    activity.filesCount === 1 ? 'file' : 'files'
  } uploaded`;
};

export const getActivityNotificationType = (activity: Activity): string => {
  // Keyed on type ahead of the entity branches, as the link target and subtitle
  // are: an analysis row is taxpayer-scoped, so it would otherwise report to
  // Mixpanel as a perm-record document upload.
  if (activity.type === ActivityType.analysisCompleted) {
    return 'Analysis completed';
  }

  if (activity.entity === ActivityEntity.taxpayer) {
    return 'Document upload to perm record';
  }

  if (activity.entity === ActivityEntity.engagement) {
    if (activity.type === ActivityType.taxQuestionnaireCompleted) {
      return 'Tax questionnaire completed';
    }

    if (activity.type === ActivityType.bankQuestionnaireCompleted) {
      return 'Bank questionnaire completed';
    }

    if (activity.type === ActivityType.finalEfileAuthCompleted) {
      return 'Final e-file auth completion';
    }

    if (activity.type === ActivityType.extensionEfileAuthCompleted) {
      return 'Extension e-file auth completion';
    }

    return 'Document upload to engagement';
  }

  if (activity.entity === ActivityEntity.workflow) {
    if (activity.type === ActivityType.fileUploaded) {
      return 'Document upload to workflow';
    }

    return 'Workflow task completed';
  }

  return '';
};

export const getFirstActivityAfterDatePid = (
  activitiesViewedAt: Date | null,
  activities: Activity[] = [],
): string | null => {
  if (!activitiesViewedAt) {
    return null;
  }

  const activity = [...activities].reverse().find((current) => {
    const createdAt = new Date(current.createdAt);
    return !!activitiesViewedAt && isBefore(activitiesViewedAt, createdAt);
  });

  return activity ? activity.pid : null;
};
